import { Anthropic } from '@anthropic-ai/sdk';
import { useEffect, useState } from 'react';

const anthropic = new Anthropic({
  apiKey: process.env.ANTHROPIC_KEY,
  dangerouslyAllowBrowser: true
});

interface Delta {
  type: string;
  text: string;
}

interface Message {
  role: 'assistant' | 'user';
  content: string;
}

interface ChatProps {
  stream?: boolean;
  messages?: Message[];
}

interface AnthropicProps {
  socket?: boolean;
  conversation?: Message[];
}

export const useAnthropic = ({ conversation, socket }: AnthropicProps) => {
  const [streamLoading, setStreamLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [error, setError] = useState({
    message: '',
    status: 0
  });

  const triggerChat = async ({ stream, messages = [] }: ChatProps) => {
    setStreamLoading(true);
    setLoading(true);
    setResponse('');

    try {
      const chat = await anthropic.messages.create({
        model: 'claude-3-5-sonnet-latest',
        max_tokens: 8192,
        stream,
        messages
      });

      let finalMessage = '';

      if (!chat) return finalMessage;

      if (stream && typeof (chat as any)[Symbol.asyncIterator] === 'function') {
        for await (const messageStream of chat as AsyncIterable<any>) {
          if (messageStream.type === 'content_block_delta') {
            const r = messageStream.delta as Delta;

            finalMessage += r?.text || '';
            setStreamLoading(false);

            setResponse(prev => (prev || '') + (r?.text || ''));
          }
        }
      } else {
        // Handle non-stream response
        // Assuming streamResponse has a 'content' property with the message text
        const message = (chat as any).content?.[0]?.text;
        if (typeof message === 'string') {
          finalMessage = message;
          setResponse(message);
        }
      }

      return finalMessage;
    } catch (err) {
      setError({
        message:
          err?.message?.message || err?.message || 'Something went wrong',
        status: err?.status || 500
      });
      return '';
    } finally {
      setLoading(false);
      setStreamLoading(false);
    }
  };

  useEffect(() => {
    if (conversation?.length)
      triggerChat({
        messages: conversation,
        stream: socket
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation]);

  return { streamLoading, loading, response, error, triggerChat };
};
